import React, { useState, useEffect, useMemo } from "react";
import {
    Plus,
    Filter,
    Download,
    Eye,
    Search,
    Calendar,
    Package,
    Truck,
    Clock,
    CheckCircle,
    AlertTriangle,
    Map,
    RefreshCw,
    ArrowUpDown,
    SortAsc,
    SortDesc,
    Star,
    Flag,
    Activity,
    Container,
    Hash,
    Ship,
    Send,
    Home,
    Store,
    User
} from "lucide-react";

import { Button, Badge, Card, Input, Select } from "../components/ui";

import { API_BASE_URL, API_ENDPOINTS } from "../constants";
import { useNavigate } from "react-router-dom";

const JobsPage = () => {
    const navigate = useNavigate();

    // State management
    const [loading, setLoading] = useState(true);
    const [jobs, setJobs] = useState([]);
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [activityCount, setActivityCount] = useState([]);
    const [selectedJobs, setSelectedJobs] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [sortField, setSortField] = useState('id');
    const [sortDirection, setSortDirection] = useState('desc');
    const [tabCount, setTabCount] = useState([]);
    const [lastRefresh, setLastRefresh] = useState(new Date());

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    // Filter states
    const [filters, setFilters] = useState({
        search: "",
        status: "",
        type: "",
        priority: "",
        customer: "",
        dateFrom: "",
        dateTo: "",
    });

    useEffect(() => {
        get_all_jobs();
        get_activity_count();

        // Set up auto-refresh every 5 minutes
        const interval = setInterval(() => {
            get_all_jobs();
            get_activity_count();
        }, 300000);

        return () => clearInterval(interval);
    }, []);

    const get_activity_count = async () => {
        try {
            const url = new URL(API_BASE_URL + API_ENDPOINTS.JOBSLISTACTIVITYCOUNT);

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                },
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            let res = await response.json();
            console.log(`Activity Response from API:`, res.data);
            setTabCount(res.data || []);
        } catch (error) {
            console.error('Error fetching activity count:', error);
            setTabCount([]);
        }
    }

    const get_all_jobs = async () => {
        try {
            setLoading(true);
            const url = new URL(API_BASE_URL + API_ENDPOINTS.JOBSLIST);

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                },
            });

            if (!response.ok)
                throw new Error(`HTTP error! status: ${response.status}`);

            let res = await response.json();
            console.log(`Response from API:`, res.data);

            if (res.data && Array.isArray(res.data)) {
                console.log(`Fetched ${res.data.length} jobs from API`);
                let all_jobs = res.data.map((row) => {
                    return {
                        id: row.job_id,
                        jobNumber: `${row.job_code}/${row.job_id}`,
                        customerPO: row.customer_po || 'N/A',
                        description: row.job_desc || 'No description',
                        type: row.job_type || 'General',
                        status: row.status === 1 ? "Active" : "Inactive",
                        statusCode: row.status,
                        statusName: row.rt_status_master?.status_name || 'Unknown',
                        statusMasterCode: row.rt_status_master?.status_code || '',
                        priority: row.priority || 'Medium',
                        tallyNo: row.tally_no || 'N/A',

                        // Dates
                        createdAt: row.created_at,
                        updatedAt: row.updated_at,
                        createdBy: row.created_by,
                        updatedBy: row.updated_by,
                        jobCreateDate: row.job_create_date,
                        jobEndDate: row.job_end_date,
                        jobActualStartDate: row.job_actual_start_date,
                        jobActualEndDate: row.job_actual_end_date,

                        // Metrics
                        totalDistance: row.total_distance,
                        totalLegs: row.total_legs,
                        totalDuration: row.total_duration,

                        // Placeholder data for compatibility with existing UI
                        customer: {
                            id: "",
                            name: "Customer Name",
                            contact: "",
                            phone: "",
                            email: "",
                            address: "",
                        },
                        vendor: {
                            id: "",
                            name: "",
                            contact: "",
                            phone: "",
                            type: "",
                        },
                        transporter: {
                            id: "",
                            name: "Transporter",
                            code: "",
                            contact: "",
                            phone: "",
                            rating: 0,
                            totalJobs: 0,
                            onTimeDelivery: 0,
                        },
                        container: {
                            id: "",
                            number: row.tally_no || 'N/A',
                            type: "",
                            owner: "",
                            status: "",
                            location: "",
                        },
                        legs: [{
                            id: 1,
                            type: row.job_type || '',
                            status: row.rt_status_master?.status_name || 'Created',
                            progress: row.status === 1 ? 10 : 0,
                            vehicle: {
                                number: 'N/A'
                            },
                            driver: {
                                name: 'N/A'
                            },
                            origin: 'Origin',
                            destination: 'Destination'
                        }],
                        route: {
                            totalDistance: row.total_distance || 0,
                            estimatedDuration: row.total_duration || 0,
                            actualDuration: 0,
                            currentLeg: 1,
                            efficiency: 0,
                        },
                        timeline: {
                            pickupDate: row.job_create_date,
                            deliveryDate: row.job_end_date,
                            estimatedDelivery: row.job_end_date,
                        },
                        pricing: {
                            estimatedCost: 0,
                            quotedPrice: 0,
                            actualCost: 0,
                        },
                        alerts: [],
                        detentionRisk: "none",
                        rating: 0,
                        specialInstructions: row.job_desc || "",
                    };
                });
                setJobs(all_jobs);
                setFilteredJobs(all_jobs);
                setLastRefresh(new Date());
            } else {
                console.error("Invalid job list format:", res.data);
                setJobs([]);
                setFilteredJobs([]);
            }
        } catch (error) {
            console.error(`Error fetching jobs:`, error);
            setJobs([]);
            setFilteredJobs([]);
        } finally {
            setLoading(false);
        }
    };

    // Job status and type configurations
    const JOB_STATUSES = {
        ACTIVE: { value: '1', label: 'Active', color: 'success', bgColor: 'bg-green-100', textColor: 'text-green-800' },
        INACTIVE: { value: '0', label: 'Inactive', color: 'danger', bgColor: 'bg-red-100', textColor: 'text-red-800' },
        ASSIGNED: { value: 'assigned', label: 'Assigned', color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
        IN_TRANSIT: { value: 'in_transit', label: 'In Transit', color: 'warning', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
        COMPLETED: { value: 'completed', label: 'Completed', color: 'success', bgColor: 'bg-green-100', textColor: 'text-green-800' },
        CANCELLED: { value: 'cancelled', label: 'Cancelled', color: 'danger', bgColor: 'bg-red-100', textColor: 'text-red-800' }
    };

    const JOB_TYPES = {
        import: {
            value: "import",
            label: "Import",
            icon: Ship,
        },
        export: {
            value: "export",
            label: "Export",
            icon: Send,
        },
        domestic: {
            value: "domestic",
            label: "Domestic",
            icon: Home,
        },
        vendor: {
            value: "vendor",
            label: "Vendor",
            icon: Store,
        },
        customer: {
            value: "customer",
            label: "Customer",
            icon: User,
        },
        GENERAL: {
            value: "General",
            label: "General Transport",
            icon: Truck,
        }
    };

    // Filter and search logic
    useEffect(() => {
        let filtered = [...jobs];

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter(
                (job) =>
                    job.jobNumber.toLowerCase().includes(searchTerm) ||
                    job.customerPO.toLowerCase().includes(searchTerm) ||
                    job.description.toLowerCase().includes(searchTerm) ||
                    job.tallyNo.toLowerCase().includes(searchTerm) ||
                    job.statusName.toLowerCase().includes(searchTerm)
            );
        }

        if (filters.status) {
            if (filters.status === '1') {
                filtered = filtered.filter(job => job.statusCode === 1);
            } else if (filters.status === '0') {
                filtered = filtered.filter(job => job.statusCode === 0);
            }
        }

        if (filters.type) {
            filtered = filtered.filter(job => job.type === filters.type);
        }

        if (filters.priority) {
            filtered = filtered.filter(job =>
                job.priority.toLowerCase() === filters.priority.toLowerCase()
            );
        }

        if (filters.dateFrom) {
            filtered = filtered.filter(
                (job) => new Date(job.createdAt) >= new Date(filters.dateFrom)
            );
        }

        if (filters.dateTo) {
            filtered = filtered.filter(job =>
                new Date(job.createdAt) <= new Date(filters.dateTo)
            );
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];

            if (sortField === "createdAt" || sortField === "updatedAt" || sortField === "created_at") {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            }

            if (sortDirection === "asc") {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });

        setFilteredJobs(filtered);
        setCurrentPage(1);
    }, [jobs, filters, sortField, sortDirection]);

    // Pagination logic
    const paginatedJobs = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return filteredJobs.slice(startIndex, startIndex + pageSize);
    }, [filteredJobs, currentPage, pageSize]);

    // Total pages calculation
    const totalPages = Math.ceil(filteredJobs.length / pageSize);

    // Event handlers
    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
    };

    const handleSelectJob = (jobId) => {
        setSelectedJobs((prev) =>
            prev.includes(jobId)
                ? prev.filter((id) => id !== jobId)
                : [...prev, jobId]
        );
    };

    const handleSelectAll = () => {
        if (selectedJobs.length === paginatedJobs.length) {
            setSelectedJobs([]);
        } else {
            setSelectedJobs(paginatedJobs.map((job) => job.id));
        }
    };

    const clearFilters = () => {
        setFilters({
            search: "",
            status: "",
            type: "",
            priority: "",
            customer: "",
            dateFrom: "",
            dateTo: "",
        });
    };

    const exportData = () => {
        const csvContent =
            "data:text/csv;charset=utf-8," +
            "Job Number,Job ID,Customer PO,Type,Status,Priority,Tally No,Description,Created At,Created By\n" +
            filteredJobs
                .map(
                    (job) =>
                        `"${job.jobNumber}",${job.id},"${job.customerPO}","${job.type}","${job.statusName}","${job.priority}","${job.tallyNo}","${job.description}","${new Date(job.createdAt).toLocaleDateString()}","${job.createdBy}"`
                )
                .join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "jobs-export.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Utility functions
    const getStatusConfig = (status) => {
        const config = JOB_STATUSES[status.toUpperCase()];
        return (
            config || {
                color: "gray",
                label: status,
                bgColor: "bg-gray-100",
                textColor: "text-gray-800",
            }
        );
    };

    const getPriorityConfig = (priority) => {
        if (!priority) return { color: "gray", label: "Medium", icon: Star };

        const configs = {
            urgent: { color: "red", label: "Urgent", icon: AlertTriangle },
            high: { color: "red", label: "High", icon: Flag },
            medium: { color: "yellow", label: "Medium", icon: Star },
            low: { color: "green", label: "Low", icon: CheckCircle },
        };
        return configs[priority.toLowerCase()] || configs.medium;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="text-lg font-medium text-gray-700">Loading jobs...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 mx-auto space-y-6">
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                            <Package className="w-8 h-8 mr-3 text-blue-600" />
                            Jobs Management
                            {/* <div className="ml-3 flex items-center">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2" />
                                <span className="text-sm font-normal text-gray-600">
                                    Last Updated: {lastRefresh.toLocaleTimeString()}
                                </span>
                            </div> */}
                        </h1>
                    </div>
                </div>

                <div className="flex items-center gap-3 mt-4 lg:mt-0">
                    <Button
                        variant="outline"
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <Filter className="w-4 h-4 mr-2" />
                        Filters{" "}
                        {Object.values(filters).filter(Boolean).length > 0 &&
                            `(${Object.values(filters).filter(Boolean).length})`}
                    </Button>

                    <Button variant="outline" onClick={exportData}>
                        <Download className="w-4 h-4 mr-2" />
                        Export
                    </Button>

                    <Button
                        variant="outline"
                        onClick={() => {
                            get_all_jobs();
                            get_activity_count();
                        }}
                        disabled={loading}
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>

                    <Button
                        variant="primary"
                        onClick={() => navigate('/jobs/create')}
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        New Job
                    </Button>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleFilterChange('status', '')}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-2xl font-bold text-gray-900">{jobs.length}</p>
                            <p className="text-sm text-gray-600">Total Jobs</p>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <Package className="w-5 h-5 text-blue-800" />
                        </div>
                    </div>
                </Card>

                <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleFilterChange('status', '1')}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-2xl font-bold text-gray-900">
                                {jobs.filter(job => job.statusCode === 1).length}
                            </p>
                            <p className="text-sm text-gray-600">Active Jobs</p>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                            <Activity className="w-5 h-5 text-green-800" />
                        </div>
                    </div>
                </Card>

                <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleFilterChange('priority', 'High')}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-2xl font-bold text-gray-900">
                                {jobs.filter(job => job.priority?.toLowerCase() === 'high').length}
                            </p>
                            <p className="text-sm text-gray-600">High Priority</p>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                            <Flag className="w-5 h-5 text-red-800" />
                        </div>
                    </div>
                </Card>

                <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleFilterChange('type', 'FTL')}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-2xl font-bold text-gray-900">
                                {jobs.filter(job => job.type === 'FTL').length}
                            </p>
                            <p className="text-sm text-gray-600">FTL Jobs</p>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                            <Truck className="w-5 h-5 text-yellow-800" />
                        </div>
                    </div>
                </Card>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
                <Card className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <Filter className="w-5 h-5 mr-2 text-blue-600" />
                            Advanced Filters
                        </h3>
                        <Button variant="outline" onClick={clearFilters}>
                            Clear All Filters
                        </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        <Input
                            label="Search"
                            placeholder="Job number, PO, description..."
                            value={filters.search}
                            onChange={(e) => handleFilterChange("search", e.target.value)}
                            icon={Search}
                        />

                        <Select
                            label="Status"
                            value={filters.status}
                            onChange={(e) => handleFilterChange("status", e.target.value)}
                            options={[
                                { value: '', label: 'All Statuses' },
                                { value: '1', label: 'Active' },
                                { value: '0', label: 'Inactive' }
                            ]}
                        />

                        <Select
                            label="Job Type"
                            value={filters.type}
                            onChange={(e) => handleFilterChange("type", e.target.value)}
                            options={[
                                { value: '', label: 'All Types' },
                                ...Object.values(JOB_TYPES).map((type) => ({
                                    value: type.value,
                                    label: type.label,
                                }))
                            ]}
                        />

                        <Select
                            label="Priority"
                            value={filters.priority}
                            onChange={(e) => handleFilterChange("priority", e.target.value)}
                            options={[
                                { value: '', label: 'All Priorities' },
                                { value: "High", label: "High Priority" },
                                { value: "Medium", label: "Medium Priority" },
                                { value: "Low", label: "Low Priority" },
                            ]}
                        />

                        <Input
                            label="Date From"
                            type="date"
                            value={filters.dateFrom}
                            onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
                            icon={Calendar}
                        />

                        <Input
                            label="Date To"
                            type="date"
                            value={filters.dateTo}
                            onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                            icon={Calendar}
                        />
                    </div>
                </Card>
            )}

            {/* Bulk Actions */}
            {selectedJobs.length > 0 && (
                <Card className="p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                            <span className="text-sm text-gray-600">
                                {selectedJobs.length} job(s) selected
                            </span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Button variant="outline" size="sm" onClick={exportData}>
                                <Download className="w-4 h-4 mr-2" />
                                Export Selected
                            </Button>
                        </div>
                    </div>
                </Card>
            )}

            {/* Jobs Table */}
            <Card>
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                                <Package className="w-5 h-5 mr-2 text-blue-600" />
                                Jobs List ({filteredJobs.length} total)
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                                Showing {(currentPage - 1) * pageSize + 1} -{" "}
                                {Math.min(currentPage * pageSize, filteredJobs.length)} of{" "}
                                {filteredJobs.length} jobs
                            </p>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSort("created_at")}
                            >
                                <ArrowUpDown className="w-4 h-4 mr-2" />
                                Sort by Date
                                {sortField === "created_at" &&
                                    (sortDirection === "asc" ? (
                                        <SortAsc className="w-4 h-4 ml-1" />
                                    ) : (
                                        <SortDesc className="w-4 h-4 ml-1" />
                                    ))}
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    <input
                                        type="checkbox"
                                        checked={
                                            selectedJobs.length === paginatedJobs.length &&
                                            paginatedJobs.length > 0
                                        }
                                        onChange={handleSelectAll}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Job Details
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status & Priority
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Information
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Timeline
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedJobs.map((job, jobIndex) => {
                                const statusConfig = getStatusConfig(job.status);
                                const priorityConfig = getPriorityConfig(job.priority);
                                const PriorityIcon = priorityConfig.icon;
                                const TypeIcon = JOB_TYPES[job.type]?.icon || Package;

                                return (
                                    <tr key={job.id} className="hover:bg-gray-50">
                                        {/* Select Checkbox */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <input
                                                type="checkbox"
                                                checked={selectedJobs.includes(job.id)}
                                                onChange={() => handleSelectJob(job.id)}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                        </td>

                                        {/* Job Details */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                                    <TypeIcon className="w-5 h-5 text-blue-600" />
                                                </div>
                                                <div className="ml-3">
                                                    <div className="flex items-center">
                                                        <button
                                                            onClick={() => navigate(`/jobs/${job.id}`)}
                                                            className="font-medium text-blue-600 hover:text-blue-800"
                                                        >
                                                            {job.jobNumber}
                                                        </button>
                                                    </div>
                                                    <p className="text-sm text-gray-500">
                                                        {JOB_TYPES[job.type]?.label || job.type}
                                                    </p>
                                                    <p className="text-xs text-gray-400">PO: {job.customerPO}</p>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Status & Priority */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="space-y-2">
                                                <Badge
                                                    variant={statusConfig.color}
                                                    className={`${statusConfig.bgColor} ${statusConfig.textColor}`}
                                                >
                                                    {job.statusName}
                                                </Badge>
                                                {job.priority && (
                                                    <div className="flex items-center">
                                                        <Badge variant={priorityConfig.color} size="sm">
                                                            <PriorityIcon className="w-3 h-3 mr-1" />
                                                            {priorityConfig.label}
                                                        </Badge>
                                                    </div>
                                                )}
                                            </div>
                                        </td>

                                        {/* Information */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="space-y-1">
                                                <div className="text-sm text-gray-900">
                                                    <Hash className="w-3 h-3 inline mr-1" />
                                                    Tally: {job.tallyNo}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    Created by: {job.createdBy}
                                                </div>
                                                <div className="text-xs text-gray-500 truncate max-w-xs">
                                                    {job.description}
                                                </div>
                                                {job.totalDistance && (
                                                    <div className="text-xs text-gray-500">
                                                        <Map className="w-3 h-3 inline mr-1" />
                                                        {job.totalDistance} km
                                                    </div>
                                                )}
                                            </div>
                                        </td>

                                        {/* Timeline */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="space-y-1">
                                                <div className="text-xs text-gray-500">
                                                    <Calendar className="w-3 h-3 inline mr-1" />
                                                    Created: {new Date(job.createdAt).toLocaleDateString()}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {new Date(job.createdAt).toLocaleTimeString([], {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}
                                                </div>
                                                {job.jobCreateDate && (
                                                    <div className="text-xs text-gray-500">
                                                        <Clock className="w-3 h-3 inline mr-1" />
                                                        Start: {new Date(job.jobCreateDate).toLocaleDateString()}
                                                    </div>
                                                )}
                                                {job.jobEndDate && (
                                                    <div className="text-xs text-gray-500">
                                                        End: {new Date(job.jobEndDate).toLocaleDateString()}
                                                    </div>
                                                )}
                                            </div>
                                        </td>

                                        {/* Actions */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center space-x-1">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => navigate(`/jobs/${job.id}`)}
                                                    title="View Details"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                                {/* <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => navigate(`/jobs/edit/${job.id}`)}
                                                    title="Edit Job"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </Button> */}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Empty State */}
                {paginatedJobs.length === 0 && !loading && (
                    <div className="text-center py-12">
                        <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            No jobs found
                        </h3>
                        <p className="text-gray-600 mb-4">
                            {filters.search || filters.status || filters.type || filters.priority
                                ? "No jobs match your current filter criteria."
                                : "No jobs have been created yet."
                            }
                        </p>
                        <div className="flex justify-center space-x-2">
                            {(filters.search || filters.status || filters.type || filters.priority) && (
                                <Button variant="outline" onClick={clearFilters}>
                                    Clear Filters
                                </Button>
                            )}
                            <Button variant="primary" onClick={() => navigate('/jobs/create')}>
                                <Plus className="w-4 h-4 mr-2" />
                                Create New Job
                            </Button>
                        </div>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div className="flex-1 flex justify-between items-center">
                            <div className="flex items-center">
                                <p className="text-sm text-gray-700">
                                    Showing{" "}
                                    <span className="font-medium">
                                        {(currentPage - 1) * pageSize + 1}
                                    </span>{" "}
                                    to{" "}
                                    <span className="font-medium">
                                        {Math.min(currentPage * pageSize, filteredJobs.length)}
                                    </span>{" "}
                                    of <span className="font-medium">{filteredJobs.length}</span>{" "}
                                    results
                                </p>
                                <select
                                    value={pageSize}
                                    onChange={(e) => setPageSize(Number(e.target.value))}
                                    className="ml-4 text-sm border border-gray-300 rounded px-2 py-1"
                                >
                                    <option value={10}>10 per page</option>
                                    <option value={20}>20 per page</option>
                                    <option value={50}>50 per page</option>
                                    <option value={100}>100 per page</option>
                                </select>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </Button>

                                <div className="flex space-x-1">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        const page = Math.max(1, currentPage - 2) + i;
                                        if (page > totalPages) return null;

                                        return (
                                            <Button
                                                key={page}
                                                variant={currentPage === page ? "primary" : "outline"}
                                                size="sm"
                                                onClick={() => setCurrentPage(page)}
                                            >
                                                {page}
                                            </Button>
                                        );
                                    })}
                                </div>

                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        setCurrentPage(Math.min(totalPages, currentPage + 1))
                                    }
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </Card>

            {/* Loading overlay for refresh */}
            {loading && jobs.length > 0 && (
                <div className="fixed top-4 right-4 bg-white rounded-lg shadow-md p-3 z-50">
                    <div className="flex items-center space-x-2">
                        <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />
                        <span className="text-sm text-gray-600">Refreshing...</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobsPage;